import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzTabsModule, NzTabChangeEvent } from 'ng-zorro-antd/tabs';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { ManageGraphComponent } from '../../../../../insights-data/manage-graph.component';
import { CommonModule } from '@angular/common';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  subYears,
  format,
  parse,
} from 'date-fns';
import {
  Subject,
  take,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
} from 'rxjs';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { InsightsDataService } from '../../../../../insights-data/insights-data.service';
import { ErrorResponse } from '../../../../../../../../core/models/error.models';
import {
  BehaviorGraphDataResponse,
  DataSheetResult,
  GraphFilterEvent,
} from '../../../../../insights-data/insights-data.interface';
import { FrequencyDatasheetGraphComponent } from '../../../../../insights-data/data-graph/frequency-datasheet-graph/frequency-datasheet-graph.component';
import { FrequencyIntervalGraphComponent } from '../../../../../insights-data/data-graph/frequency-interval-graph/frequency-interval-graph.component';
import { LatencyGraphComponent } from '../../../../../insights-data/data-graph/latency-graph/latency-graph.component';
import { PartialIntervalGraphComponent } from '../../../../../insights-data/data-graph/partial-interval-graph/partial-interval-graph.component';
import { ReplacementBehaviorGraphComponent } from '../../../../../insights-data/data-graph/replacement-behavior-graph/replacement-behavior-graph.component';
import { LoaderService } from '../../../../../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { GraphFilterComponent } from '../../../../../insights-data/data-graph/graph-filter/graph-filter.component';
import { DATE_FORMAT } from '../../../../feeback.enum';

@Component({
  selector: 'app-behavior-graph',
  standalone: true,
  imports: [
    NzSegmentedModule,
    NzTabsModule,
    FormsModule,
    NzDividerComponent,
    ReactiveFormsModule,
    CommonModule,
    FrequencyDatasheetGraphComponent,
    FrequencyIntervalGraphComponent,
    LatencyGraphComponent,
    PartialIntervalGraphComponent,
    ReplacementBehaviorGraphComponent,
    NzSpinModule,
    GraphFilterComponent,
  ],
  templateUrl: './behavior-graph.component.html',
  styleUrls: ['./behavior-graph.component.scss'],
})
export class BehaviorGraphComponent
  extends ManageGraphComponent
  implements OnDestroy, OnInit, OnChanges
{
  @Input() bhevaiorId = 0;
  private readonly destroy$ = new Subject<void>();
  graphForm!: FormGroup;
  datasheetData: { id: number; value: string }[] = [
    { id: 0, value: 'Frequency/Duration Based Datasheet Graph' },
    { id: 1, value: 'Frequency Interval' },
    { id: 2, value: 'Latency' },
    { id: 3, value: 'Partial Interval' },
    { id: 4, value: 'Replacement Behavior' },
  ];

  selectedTabIndex = 0;
  loading$: Observable<boolean> = of(false);
  responseData: DataSheetResult | null = null;
  private preventApiCall = false;
  private isUpdatingTab = false;
  private hasGraphFilterAssigned = false;

  constructor(
    private readonly fb: FormBuilder,
    cd: ChangeDetectorRef,
    private readonly commonService: CommonService,
    private readonly dataService: InsightsDataService,
    private readonly loaderService: LoaderService
  ) {
    super(cd);
    const date = new Date();
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
    this.loading$ = this.loaderService.loading$;
    this.graphForm = this.fb.group({
      datasheet: [1, [Validators.required]],
      graph: [1],
      dateRange: [
        [format(weekStart, DATE_FORMAT), format(weekEnd, DATE_FORMAT)],
        [Validators.required],
      ],
      startDate: [weekStart],
      endDate: [weekEnd],
      selectedTimeRange: [1, Validators.required],
      graphImage: [''],
      graphImages: [[]],
      graphImageTotal: [''],
      graphImagesTotal: [[]],
      viewType: [1],
      isTrendLineEnabled: [false],
    });

    this.onTimeRangeChange(this.value('selectedTimeRange'));
  }

  ngOnInit(): void {
    this.setupFormSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bhevaiorId'] && this.hasGraphFilterAssigned) {
      this.getGraphData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFilterChange(event: GraphFilterEvent) {
    this.hasGraphFilterAssigned = true;

    this.graphForm.patchValue(
      {
        viewType: event.viewType,
        isTrendLineEnabled: event.isTrendLineEnabled,
        dateRange: [event.startDate, event.endDate],
        startDate: parse(event.startDate, DATE_FORMAT, new Date()),
        endDate: parse(event.endDate, DATE_FORMAT, new Date()),
        selectedTimeRange: event.selectedTimeOption ?? 1,
      },
      { emitEvent: false }
    );

    if (
      this.bhevaiorId > 0 &&
      this.isFormValidForApi() &&
      !this.loaderService.loadingSubject.getValue() &&
      !this.preventApiCall &&
      !this.isUpdatingTab
    ) {
      this.getGraphData();
    }
  }

  private setupFormSubscriptions(): void {
    const relevantControls = [
      'datasheet',
      'dateRange',
      'viewType',
      'isTrendLineEnabled',
      'startDate',
      'endDate',
    ];

    for (const controlName of relevantControls) {
      this.graphForm
        .get(controlName)
        ?.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          if (
            this.bhevaiorId > 0 &&
            this.isFormValidForApi() &&
            !this.loaderService.loadingSubject.getValue() &&
            !this.preventApiCall &&
            !this.isUpdatingTab
          ) {
            this.getGraphData();
          }
        });
    }
  }

  private isFormValidForApi(): boolean {
    const formValue = this.graphForm.value;
    const hasDatasheet =
      formValue.datasheet !== null && formValue.datasheet !== undefined;
    const hasValidDateRange =
      formValue.dateRange?.[0] && formValue.dateRange?.[1];

    return hasDatasheet && Boolean(hasValidDateRange);
  }

  onTimeRangeChange(value: number): void {
    this.graphForm.patchValue({ selectedTimeRange: value });

    const today = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (value) {
      case 1:
        startDate = startOfWeek(today, { weekStartsOn: 1 });
        endDate = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case 2:
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case 3:
        startDate = startOfDay(subMonths(today, 6));
        endDate = endOfDay(today);
        break;
      case 4:
        startDate = startOfDay(subYears(today, 1));
        endDate = endOfDay(today);
        break;
      case 0:
      default:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
    }

    const formattedStartDate = format(startDate, DATE_FORMAT);
    const formattedEndDate = format(endDate, DATE_FORMAT);

    this.graphForm.patchValue({
      dateRange: [formattedStartDate, formattedEndDate],
      startDate: startDate,
      endDate: endDate,
    });
  }

  onTabChange(event?: NzTabChangeEvent | number): void {
    if (this.isUpdatingTab) return;

    this.isUpdatingTab = true;

    if (event !== undefined) {
      this.selectedTabIndex =
        typeof event === 'number' ? event : (event.index ?? 0);
    }

    switch (this.selectedTabIndex) {
      case 0:
        this.graphForm.patchValue(
          {
            datasheet: 1,
            graph: 1,
          },
          { emitEvent: false }
        );
        break;
      case 1:
        this.graphForm.patchValue(
          {
            datasheet: 1,
            graph: 2,
          },
          { emitEvent: false }
        );
        break;
      case 2:
        this.graphForm.patchValue(
          {
            datasheet: 2,
            graph: null,
          },
          { emitEvent: false }
        );
        break;
      case 3:
        this.graphForm.patchValue(
          {
            datasheet: 3,
            graph: null,
          },
          { emitEvent: false }
        );
        break;
      case 4:
        this.graphForm.patchValue(
          {
            datasheet: 4,
            graph: null,
          },
          { emitEvent: false }
        );
        break;
      default:
        break;
    }

    setTimeout(() => {
      this.isUpdatingTab = false;
      if (
        this.bhevaiorId > 0 &&
        !this.loaderService.loadingSubject.getValue()
      ) {
        this.getGraphData();
      }
    }, 0);
  }

  value(controlName: string) {
    return this.graphForm.get(controlName)?.value;
  }

  getGraphData() {
    this.isGraphDataAvailable = false;
    this.loaderService.show();
    this.preventApiCall = true;

    // Ensure required params are present before building query
    const unitName = this.value('datasheet');
    if (unitName === null || unitName === undefined || this.bhevaiorId <= 0) {
      this.loaderService.hide();
      this.preventApiCall = false;
      return;
    }

    const startFromControl = this.graphForm.get('startDate')?.value;
    const endFromControl = this.graphForm.get('endDate')?.value;
    const range = this.graphForm.get('dateRange')?.value || ['', ''];

    const parsedRangeStart = range[0]
      ? parse(range[0], DATE_FORMAT, new Date())
      : null;
    const parsedRangeEnd = range[1]
      ? parse(range[1], DATE_FORMAT, new Date())
      : null;

    const startDateVal = startFromControl
      ? new Date(startFromControl)
      : parsedRangeStart;
    const endDateVal = endFromControl
      ? new Date(endFromControl)
      : parsedRangeEnd;

    if (
      !startDateVal ||
      !endDateVal ||
      Number.isNaN(startDateVal.getTime()) ||
      Number.isNaN(endDateVal.getTime())
    ) {
      this.loaderService.hide();
      this.preventApiCall = false;
      return;
    }

    this.dataService
      .getBehaviorGraphData({
        CaseBehaviorId: this.bhevaiorId,
        UnitName: unitName,
        StartDate: format(startDateVal, DATE_FORMAT),
        EndDate: format(endDateVal, DATE_FORMAT),
        graphType: this.value('graph'),
        viewType: this.graphForm.get('viewType')?.value,
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          if (res.result) {
            this.manageBehvaiorGraphResponseData(
              res.result,
              this.value('datasheet'),
              this.value('graph')
            );
          } else {
            this.loaderService.hide();
            this.displayNoData = true;
            this.cd.markForCheck();
          }
          setTimeout(() => {
            this.preventApiCall = false;
          }, 1000);
          this.loaderService.hide();
        },
        error: (error: ErrorResponse) => {
          this.loaderService.hide();
          this.preventApiCall = false;
          this.commonService.notify('error', error.message);
        },
      });
  }

  async manageBehvaiorGraphResponseData(
    data: BehaviorGraphDataResponse,
    selectedUnit: number,
    graphType: number
  ) {
    await this.processBehaviorGraphData(
      data,
      selectedUnit,
      graphType,
      this.graphForm?.get('viewType')?.value,
      this.graphForm?.get('isTrendLineEnabled')?.value || false
    );
  }
}
